import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [filteredArticles, setFilteredArticles] = useState(articles);

  useEffect(() => {
    setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    if (slug === 'all') {
      setFilteredArticles(articles);
    } else {
      setFilteredArticles(articles.filter(a => a.category === slug));
    }
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === slug) || categories[0];
  const categoryName = currentCategory.name;

  return (
    <>
      <SEO
        title={`${categoryName} - 91n.com 分类浏览 | 海量优质内容`}
        description={`浏览91n.com ${categoryName}分类下的所有精彩内容。探索最新、最热门的文章和资讯，尽在91n.com。`}
        keywords={`91n.com,${categoryName},分类浏览,文章列表`}
        canonicalUrl={`https://91n.com/category/${slug}`}
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91n.com' },
            { '@type': 'ListItem', position: 2, name: categoryName, item: `https://91n.com/category/${slug}` },
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${categoryName} - 91n.com`,
          url: `https://91n.com/category/${slug}`,
          isPartOf: { '@type': 'WebSite', name: '91n.com', url: 'https://91n.com' },
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100/60 border-b border-background-200/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/all' },
                { label: categoryName },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mt-4">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-600 mt-2">
              共 {filteredArticles.length} 篇文章
            </p>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`} />
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl" />
              </div>
              <p className="text-foreground-600 text-sm">该分类下暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/50 overflow-hidden transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-xs font-medium bg-background-50/90 text-foreground-700 rounded-full">
                        {article.tags[0]}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h2 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 mb-4 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line" /> {article.readTime}
                      </span>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}